import { api } from "./api";

/**
 * REST client for the {@code /api/v1/recycle} endpoints. Mirrors the
 * Java {@code RecycleController} wire shapes — keep these typed
 * records in sync with the controller's response/request records or
 * the UI silently breaks on field renames.
 */

export type DeletedObject = {
  storeId: string;
  name: string | null;
  objectClass: string | null;
  displayPath: string | null;
  searchPath: string | null;
  originalParentStoreId: string | null;
  originalParentDisplayPath: string | null;
  originalParentSearchPath: string | null;
  /** ISO-8601 string from Java's Instant. */
  deletedAt: string | null;
  deletedByUsername: string | null;
  ownerUsername: string | null;
  /** "recoverable" | "past_retention" | "unknown" */
  recoverability: string;
  parentStillExists: boolean;
};

export type RecycleListResult = {
  objects: DeletedObject[];
  totalMatched: number;
  windowDescription: string;
  recoverableOnly: boolean;
};

export type RecycleListQuery = {
  scopePath?: string;
  /** ISO 8601 duration like "P30D". */
  deletedSince?: string;
  deletedBy?: string;
  recoverableOnly?: boolean;
  limit?: number;
};

export async function fetchRecycleList(q: RecycleListQuery): Promise<RecycleListResult> {
  const params: Record<string, string> = {};
  if (q.scopePath && q.scopePath.trim()) params.scopePath = q.scopePath.trim();
  if (q.deletedSince) params.deletedSince = q.deletedSince;
  if (q.deletedBy && q.deletedBy.trim()) params.deletedBy = q.deletedBy.trim();
  if (q.recoverableOnly !== undefined) params.recoverableOnly = String(q.recoverableOnly);
  if (q.limit !== undefined) params.limit = String(q.limit);
  const { data } = await api.get<RecycleListResult>("/recycle", { params });
  return data;
}

export type RecycleDetailsResult = {
  object: DeletedObject;
  restoreTargetPath: string | null;
  restoreTargetCollides: boolean;
  collisionStoreId: string | null;
  blockers: string[];
};

export async function fetchRecycleDetails(storeId: string): Promise<RecycleDetailsResult> {
  const { data } = await api.get<RecycleDetailsResult>(`/recycle/${encodeURIComponent(storeId)}`);
  return data;
}

export type RestoreBody = {
  alternateParentStoreId?: string | null;
  alternateName?: string | null;
  reason: string;
};

export type RestoreResponse = {
  storeId: string;
  name: string;
  restoredToPath: string;
  restoredAt: string;
  restoredBy: string;
  wasRenamed: boolean;
  previousName: string | null;
};

export async function restoreOne(storeId: string, body: RestoreBody): Promise<RestoreResponse> {
  const { data } = await api.post<RestoreResponse>(
    `/recycle/${encodeURIComponent(storeId)}/restore`,
    body,
  );
  return data;
}

export type BulkItem = {
  storeId: string;
  alternateParentStoreId?: string | null;
  alternateName?: string | null;
};

export type BulkBody = {
  items: BulkItem[];
  reason: string;
};

/** Per-item bulk outcome — matches Java's {@code BulkItemOutcome}. */
export type BulkItemOutcome = {
  storeId: string | null;
  /** "OK" or one of the failure-mode codes. */
  status: string;
  name: string | null;
  restoredToPath: string | null;
  errorMessage: string | null;
};

export type BulkResponse = {
  outcomes: BulkItemOutcome[];
  succeeded: number;
  failed: number;
  elapsedMs: number;
};

export async function restoreBulk(body: BulkBody): Promise<BulkResponse> {
  const { data } = await api.post<BulkResponse>("/recycle/restore-bulk", body);
  return data;
}
